import type { LearningAccelerationReport, LearningStatus } from '../shared/desktop-api';
import { learningCount } from '../shared/learning-skills';
export const learningNumber = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString();

/** Availability is distinct from either a confirmation count or recorded use. */
export function hasAvailableLearningPattern(
  report: LearningAccelerationReport | null,
): boolean | null {
  if (!report) return null;
  const positive = (value: unknown) => (learningCount(value) ?? 0) > 0;
  if (positive(report.scalarFields.availableFieldRoutes)) return true;
  // Still counting page designs: their zeros are placeholders, so only scalar routes can answer.
  if (report.layouts.summaryPending === true) return null;
  return (
    positive(report.layouts.typed) ||
    report.layouts.top.some(
      (layout) => positive(layout.tableMapBuiltFrom) || positive(layout.numericTableMapBuiltFrom),
    )
  );
}

/** Whether the layout counts in this report are measurements at all (see `summaryPending`). */
export function hasLayoutCounts(report: LearningAccelerationReport | null): boolean {
  return !!report && !report.layouts.summaryPending;
}

/** Dispatch/attempt counts include operational failures. Only a retained
 * challenger measurement proves that an improvement was tested; a baseline
 * champion alone does not. A measured decline still counts as a test. */
export function hasMeasuredLearningTrial(status: LearningStatus | null | undefined): boolean {
  if (!status) return false;
  const candidates = [
    ...(status.recent ?? []),
    ...(status.lastAppliedTypeComparison ?? []),
    ...(status.retiredTypeComparisons ?? []).flatMap((comparison) => comparison.attempts),
  ];
  return (
    candidates.some((candidate) =>
      [candidate.mining, candidate.validation].some((score) => {
        if (!score) return false;
        const counts = [score.right, score.wrong, score.declined];
        return (
          counts.every((count) => learningCount(count) !== null) &&
          counts.some((count) => count > 0)
        );
      }),
    ) ||
    (status.comparisons ?? []).some((comparison) =>
      comparison.attempts.some(
        (attempt) => learningCount(attempt.total) !== null && attempt.total > 0,
      ),
    )
  );
}
